/** What a reading answered - **EXP-35, and ADR-0011's redaction gate reaching a reader.**
 *
 * Three answers and the second and third must never render identically:
 *
 * - **A retry.** `already_extracted` means an earlier request did the reading and this one did not.
 *   It is not a failure and not a first success: the canonical case is a request that completed at
 *   the server and timed out at a proxy.
 * - **Asked, and nothing found.** `redacted_items: 0` is a redaction that ran and removed nothing.
 * - **Nobody asked.** `redacted_items: null` is the repeat path, which read nothing, so there is no
 *   count - F10's lesson: a step nobody checked is not a step that holds, and zero would claim it had.
 *
 * `data-asked` is the structural form of that distinction, so a test or a gate asserts the fact and
 * not the wording. The three states are chips (a word and a glyph, never colour alone); what each
 * one means is behind the info control, which is where the long sentences went.
 */

import type { Extracted } from "../../api/evidence";
import { InfoPopover, StatusChip } from "../../components/ui";

export function ReadOutcome({ outcome }: { outcome: Extracted }) {
  if (outcome.already_extracted) {
    return (
      <span data-testid="already-extracted" data-asked="no">
        <StatusChip tone="info" size="sm">
          Already read
        </StatusChip>
        <InfoPopover infoKey="processing.read-outcome" />
      </span>
    );
  }
  const removed = outcome.redacted_items;
  return (
    <span
      data-testid="document-read"
      data-asked={removed == null ? "no" : "yes"}
      data-redacted={removed == null ? "" : String(removed)}
    >
      <StatusChip tone="ok" size="sm">
        {removed == null
          ? "Read, no count"
          : removed === 0
            ? "Read, nothing removed"
            : `Read, ${String(removed)} removed`}
      </StatusChip>
      <InfoPopover infoKey="processing.read-outcome" />
    </span>
  );
}
